import { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone or TWA)
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    // Detect platform
    const ua = navigator.userAgent || '';
    const isIOSDevice =
      (/iPad|iPhone|iPod/.test(ua) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) &&
      !window.MSStream;
    const isAndroidDevice = /Android/i.test(ua);

    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);

    // Check if user dismissed recently (re-show after 3 days)
    const dismissed = localStorage.getItem('sma-install-dismissed');
    const canShow = !dismissed || Date.now() - parseInt(dismissed) > 3 * 24 * 60 * 60 * 1000;

    // Show prompt for mobile users after a short delay
    if (canShow && (isIOSDevice || isAndroidDevice)) {
      setTimeout(() => setShowPrompt(true), 2000);
    }

    // Listen for beforeinstallprompt (Android/Chrome/Edge)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // If prompt wasn't already shown, show it now
      if (canShow) {
        setShowPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Listen for successful install
    const installedHandler = () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('sma-install-dismissed', Date.now().toString());
  };

  if (isInstalled || !showPrompt) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 animate-slide-up sm:left-auto sm:right-4 sm:w-96">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4 relative overflow-hidden">
        {/* Orange accent bar */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-primary-500 to-primary-400" />

        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Dismiss"
        >
          <X size={18} />
        </button>

        <div className="flex items-start gap-3 pr-6">
          <Smartphone className="text-primary-500 flex-shrink-0" size={24} />
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
              Install SMA App
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">
              {isIOS
                ? 'Tap the Share button, then "Add to Home Screen" to install.'
                : deferredPrompt
                ? 'Get quick access from your home screen. Works offline too!'
                : 'Open browser menu (⋮) and tap "Add to Home Screen" or "Install App".'}
            </p>
          </div>
        </div>

        {!isIOS && deferredPrompt && (
          <div className="flex gap-2 mt-3 ml-15">
            <button
              onClick={handleDismiss}
              className="flex-1 px-3 py-2 text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              Not now
            </button>
            <button
              onClick={handleInstall}
              className="flex-1 px-3 py-2 text-xs font-medium text-white bg-primary-500 hover:bg-primary-600 transition-colors flex items-center justify-center gap-1.5"
            >
              <Download size={14} />
              Install
            </button>
          </div>
        )}

        {!isIOS && !deferredPrompt && (
          <div className="mt-3 ml-15 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 px-3 py-2">
            <span>Tap</span>
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="5" r="2"/>
              <circle cx="12" cy="12" r="2"/>
              <circle cx="12" cy="19" r="2"/>
            </svg>
            <span>then <strong>"Add to Home Screen"</strong></span>
          </div>
        )}

        {isIOS && (
          <div className="mt-3 ml-15 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 px-3 py-2">
            <span>Tap</span>
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2l3 3h-2v6h-2V5H9l3-3zm-7 9h2v10h10V11h2v10c0 1.1-.9 2-2 2H7c-1.1 0-2-.9-2-2V11z"/>
            </svg>
            <span>then <strong>"Add to Home Screen"</strong></span>
          </div>
        )}
      </div>
    </div>
  );
}
